/**
 * float — the card that carries an event out beyond the ring.
 *
 * Drawn in SCREEN pixels around its own origin: the scene owns the slot, puts
 * it at the rotated world point and applies scale(1 / camera.scale), so the
 * card must never translate itself. Sizes here have to match the `--float-w` /
 * `--float-h` tokens the scene feeds to the layout algorithm.
 */
import type { FloatCtx, Fragment } from '../types';
import { n, svgEl } from '../../core/svg';
import { avatarDisc, ellipsize } from './avatar';
import { markSizeFactor } from './mark';
import { FLOAT } from './metrics';

const W = FLOAT.width;
const H = FLOAT.height;
const AVATAR_R = 14;
const AVATAR_CX = -W / 2 + 8 + AVATAR_R;
const TEXT_X = AVATAR_CX + AVATAR_R + 8;
const TITLE_MAX = 17;
/**
 * The larger look at a face while the pointer rests on its card. Three times
 * the disc: the stored photo is a 512 px square, so at 88 px it is still sharper
 * than the screen, and a face reads as a face rather than a thumbnail.
 */
export const PREVIEW_R = 44;
const PREVIEW_GAP = 8;

export const floatFragment: Fragment<FloatCtx> = (ctx, state) => {
  const hot = state.hover || state.selected;
  const colour = ctx.colour ?? 'var(--accent)';
  const orphaned = ctx.orphaned ?? Boolean(ctx.event.orphanedAt);

  // Prominence sizes the card the same way it sizes the mark, and the scene
  // feeds the packer the same factor — so a loud circle's floats claim the room
  // they are drawn in rather than overlapping their neighbours.
  const size = markSizeFactor(ctx.style?.prominence);

  const g = svgEl('g', {
    'data-frag': 'float',
    opacity: orphaned ? 0.45 : 1,
    ...(size === 1 ? {} : { transform: `scale(${size.toFixed(3)})` }),
  });

  if (hot) {
    g.append(
      svgEl('rect', {
        x: n(-W / 2 - 3),
        y: n(-H / 2 - 3),
        width: n(W + 6),
        height: n(H + 6),
        rx: 13,
        fill: 'none',
        stroke: colour,
        'stroke-width': 1,
        opacity: 0.32,
        'pointer-events': 'none',
      }),
    );
  }

  g.append(
    svgEl('rect', {
      x: n(-W / 2),
      y: n(-H / 2),
      width: n(W),
      height: n(H),
      rx: 10,
      fill: hot ? 'var(--float-bg-hover)' : 'var(--float-bg)',
      stroke: hot || state.selected ? colour : 'var(--float-border)',
      'stroke-width': state.selected ? 1.6 : 1,
      ...(orphaned ? { 'stroke-dasharray': '4 3' } : {}),
      'pointer-events': 'fill',
    }),
  );

  g.append(avatarDisc({ cx: AVATAR_CX, cy: 0, r: AVATAR_R, event: ctx.event, colour }));

  // Source badge: a small colour bead on the avatar's shoulder.
  g.append(
    svgEl('circle', {
      cx: n(AVATAR_CX + AVATAR_R - 2),
      cy: n(AVATAR_R - 3),
      r: 4,
      fill: colour,
      stroke: hot ? 'var(--float-bg-hover)' : 'var(--float-bg)',
      'stroke-width': 1.6,
      'pointer-events': 'none',
    }),
  );

  const hasSub = Boolean(ctx.ageText);
  const title = svgEl('text', {
    x: n(TEXT_X),
    y: hasSub ? -2.5 : 4,
    fill: 'var(--text)',
    'font-size': 12,
    'font-weight': 500,
    'pointer-events': 'none',
  });
  title.textContent = ellipsize(ctx.event.title, TITLE_MAX);
  g.append(title);

  if (ctx.ageText) {
    const sub = svgEl('text', {
      x: n(TEXT_X),
      y: 12,
      fill: 'var(--text-dim)',
      'font-size': 10,
      'letter-spacing': 0.6,
      'pointer-events': 'none',
    });
    sub.textContent = ctx.ageText;
    g.append(sub);
  }

  if (state.pinned) g.append(pinGlyph(W / 2 - 12, -H / 2 + 11));

  // Hover only, and only where there is a photo to see more of: initials at
  // three times the size say nothing the card does not. Selection has the
  // detail panel, which shows the photo already.
  if (state.hover && !state.selected && ctx.event.imageKey) g.append(preview(ctx, colour));

  return g;
};

/** The photo, large, floating just above the card and clear of the pointer. */
function preview(ctx: FloatCtx, colour: string): SVGElement {
  const cy = -H / 2 - PREVIEW_GAP - PREVIEW_R;
  return svgEl(
    'g',
    { class: 'float__preview', 'data-role': 'preview', 'pointer-events': 'none' },
    [
      svgEl('circle', {
        cx: n(AVATAR_CX),
        cy: n(cy),
        r: n(PREVIEW_R + 3),
        fill: 'var(--float-bg-hover)',
        stroke: colour,
        'stroke-width': 1,
        opacity: 0.9,
      }),
      avatarDisc({ cx: AVATAR_CX, cy, r: PREVIEW_R, event: ctx.event, colour, ring: 1.5, fontSize: 26 }),
    ],
  );
}

/**
 * A pin head with a short stem, so a pinned card is legible without a legend —
 * and the way back out, because double-clicking a card to release it is not
 * something anyone discovers. The glyph carries a target big enough for a
 * finger and says what it does; `core/input.ts` unpins on a press inside
 * `data-role="unpin"`.
 *
 * A role is not identity: the scene stamps `data-kind` and `data-id` on the
 * card, and this names a part *within* it, which is the fragment's own to name.
 */
const UNPIN_TARGET_R = 9;

function pinGlyph(x: number, y: number): SVGElement {
  const title = svgEl('title');
  title.textContent = 'Unpin';

  return svgEl(
    'g',
    {
      'data-role': 'unpin',
      class: 'float__unpin',
      role: 'button',
      'aria-label': 'Unpin',
      'pointer-events': 'all',
    },
    [
      title,
      // The affordance: a disc the stylesheet fades in under the pointer, so a
      // resting card is still just a card with a pin on it. The attribute is
      // the floor, not the value — a presentation attribute loses to the rule.
      svgEl('circle', {
        class: 'float__unpin-bg',
        cx: n(x),
        cy: n(y),
        r: n(UNPIN_TARGET_R),
        fill: 'var(--accent)',
        opacity: 0,
        'pointer-events': 'none',
      }),
      // Transparent, and the whole of the target: `pointer-events: fill` hit
      // tests the fill area whether or not anything was painted into it.
      svgEl('circle', {
        cx: n(x),
        cy: n(y),
        r: n(UNPIN_TARGET_R),
        fill: 'transparent',
        'pointer-events': 'fill',
      }),
      svgEl('circle', { cx: n(x), cy: n(y), r: 3, fill: 'var(--accent)', 'pointer-events': 'none' }),
      svgEl('line', {
        x1: n(x),
        y1: n(y + 2.4),
        x2: n(x - 2.6),
        y2: n(y + 6.4),
        stroke: 'var(--accent)',
        'stroke-width': 1.2,
        'stroke-linecap': 'round',
        'pointer-events': 'none',
      }),
    ],
  );
}
